<script setup lang="ts">
import { computed, ref } from "vue";
import { DocumentTextIcon, ArrowPathIcon, ClipboardDocumentIcon, CheckIcon } from "@heroicons/vue/24/outline";
import type { DmMessage } from "~/types/entities";
import type { EnrichedMessage } from "~/utils/dm-view";
import { hasBubbleLinks, parseBubbleSegments } from "~/utils/dm-view";
import { buildWorkflowFlow, type WorkflowFlowBlock } from "~/utils/workflow";
import { formatTime, formatFullTime } from "~/utils/time";
import { toCanonicalUrl, toThumbUrl } from "~/utils/image";

/**
 * 单条 DM 消息（Phase 4 拆分自 KnockKnockModal）：
 * 时间分隔行 + system 分界 + 气泡（头像 / 正文 / 操作条 / 引用卡 /
 * AI 工作流时间线 / 引用来源）。纯展示组件——派生数据由父级
 * enrichedMessages 一次算好，交互全部 emit 回容器。
 */
const props = defineProps<{
  entry: EnrichedMessage;
  /** 刚复制成功的消息 documentId（操作条「已复制」反馈） */
  copiedId: string | null;
  /** 是否显示「重新生成」（仅会话最后一条 AI 回复且无流式进行中） */
  showRegenerate: boolean;
  regenerating: boolean;
}>();

const emit = defineEmits<{
  (e: "contextmenu", evt: MouseEvent, msg: DmMessage): void;
  (e: "profile", url: string | null): void;
  (e: "open-post", documentId: string): void;
  (e: "bubble-link", href: string, evt: Event): void;
  (e: "copy", msg: DmMessage): void;
  (e: "regenerate", msg: DmMessage): void;
  (e: "quote-click", msg: DmMessage): void;
}>();

const { openGallery } = useImageGallery();

/** 附图（撤回后不显示） */
const images = computed(() => (props.entry.msg.deletedAt ? [] : (props.entry.msg.images ?? [])));
/** 只发了图没写字：不渲染空的文字气泡 */
const imageOnly = computed(
  () => images.value.length > 0 && typeof props.entry.rendered === "string" && !props.entry.rendered.trim(),
);
const imagesRef = ref<HTMLElement | null>(null);

const openImages = (index: number) => {
  openGallery(
    images.value.map((image, i) => ({
      src: toCanonicalUrl(image.url),
      thumb: toThumbUrl(image.url),
      width: image.width,
      height: image.height,
      element: () => imagesRef.value?.querySelectorAll<HTMLElement>(".ik-knock__msg-image-thumb")[i],
    })),
    index,
  );
};

/** 消息时间 hover 详情：气泡 title 显示完整时间戳 */
const fullTime = computed(() => formatFullTime(props.entry.msg.createdAt));

/** 将 system 标记转换为更友好的文案 */
const SYSTEM_LABELS: Record<string, string> = {
  '[对话已重置]': '开始新话题',
};
const systemLabel = computed(
  () => SYSTEM_LABELS[props.entry.msg.content?.trim() ?? ''] ?? props.entry.msg.content,
);

/** AI 回复按真实发生顺序展开；正文可能先于首个工作流事件到达。 */
const flowBlocks = computed((): WorkflowFlowBlock[] | null => {
  const { entry } = props;
  if (!entry.aiRich || typeof entry.rendered !== "string") return null;
  return buildWorkflowFlow(entry.workflowEvents, entry.rendered, { settled: !entry.aiStreaming });
});

/** 最后一个非空文字块的下标：「(已编辑)」挂在它上面，等待气泡不触发步骤收起。 */
const lastTextIndex = computed(() => {
  const blocks = flowBlocks.value ?? [];
  for (let i = blocks.length - 1; i >= 0; i--) {
    const block = blocks[i]!;
    if (block.type === "text" && block.text) return i;
  }
  return -1;
});

/** AI 回复刚占位、什么数据都还没到 */
const awaitingFirstData = computed(
  () => props.entry.aiRich && props.entry.pendingStream && !props.entry.workflowEvents.length,
);
</script>

<template>
  <!-- AI 回复已占位、但还没收到任何数据（工作流事件或正文）：先不渲染，收到第一份数据再出现 -->
  <template v-if="!awaitingFirstData">
    <!-- 时间分隔行：首条或与上条间隔 > 5min 时显示 -->
    <div
      v-if="entry.showTime"
      class="ik-knock__time-divider"
      :class="{ 'is-new': entry.isNew }"
    >
      {{ formatTime(entry.msg.createdAt) }}
    </div>
    <!-- system 分界（如「对话已重置」3.3.4）：居中提示，不渲染气泡 -->
    <div
      v-if="entry.msg.kind === 'system'"
      class="ik-knock__sys-divider"
      :data-mid="entry.msg.documentId"
    >
      <span>{{ systemLabel }}</span>
    </div>
    <div
      v-else
      class="ik-knock__msg"
      :class="{
        'is-new': entry.isNew,
        'is-mine': entry.isMine,
      }"
      :data-mid="entry.msg.documentId"
      @contextmenu="emit('contextmenu', $event, entry.msg)"
    >
      <div
        class="ik-knock__msg-avatar"
        :class="{ 'is-clickable': entry.avatarClickable }"
        :role="entry.avatarClickable ? 'button' : undefined"
        :tabindex="entry.avatarClickable ? 0 : undefined"
        :aria-hidden="entry.avatarClickable ? undefined : 'true'"
        :aria-label="entry.avatarClickable ? `查看${entry.msg.sender?.name || '用户'}的主页` : undefined"
        @click="emit('profile', entry.profileUrl)"
        @keydown.enter="emit('profile', entry.profileUrl)"
      >
        <img
          v-if="entry.msg.sender?.avatar"
          :src="entry.msg.sender.avatar"
          :alt="entry.msg.sender?.name || ''"
          class="ik-knock__msg-avatar-img"
          draggable="false"
        />
        <img v-else src="/images/default-avatar.webp" alt="" class="ik-knock__msg-avatar-img" draggable="false" />
      </div>
      <div class="ik-knock__msg-body">
        <!-- 3.3 AI 回复：过程步骤与回答气泡按发生顺序穿插（思考原文、工具参数不下发，只展示骨架） -->
        <template v-if="flowBlocks">
          <template v-for="(block, blockIdx) in flowBlocks" :key="block.key">
            <AiStepGroup
              v-if="block.type === 'steps'"
              :steps="block.steps"
              :settled="!entry.aiStreaming"
              :followed="blockIdx < lastTextIndex"
              @open-post="emit('open-post', $event)"
            />
            <div
              v-else
              class="ik-knock__msg-bubble"
              :title="fullTime || undefined"
            >
              <AiMessageBody
                v-if="block.text"
                :text="block.text"
                :streaming="block.live"
                @open-post="emit('open-post', $event)"
              />
              <span v-else class="ik-knock__msg-typing" aria-label="正在输入">
                <span class="ik-knock__typing-dot" />
                <span class="ik-knock__typing-dot" />
                <span class="ik-knock__typing-dot" />
              </span>
              <span v-if="blockIdx === lastTextIndex && entry.msg.editedAt" class="ik-knock__msg-edited">(已编辑)</span>
            </div>
          </template>
        </template>
        <div v-if="images.length" ref="imagesRef" class="ik-knock__msg-images">
          <button
            v-for="(image, imageIndex) in images"
            :key="`${entry.msg.documentId}-image-${imageIndex}`"
            type="button"
            class="ik-knock__msg-image"
            @click="openImages(imageIndex)"
          >
            <img
              :src="toThumbUrl(image.url)"
              alt="图片"
              class="ik-knock__msg-image-thumb"
              decoding="async"
              referrerpolicy="no-referrer"
            />
          </button>
        </div>
        <div
          v-if="!flowBlocks && !imageOnly"
          class="ik-knock__msg-bubble"
          :class="{ 'is-deleted': !!entry.msg.deletedAt }"
          :title="fullTime || undefined"
        >
          <template v-if="typeof entry.rendered === 'object'">
            <CommentBody :content="entry.rendered.content" />
          </template>
          <span
            v-else-if="entry.pendingStream"
            class="ik-knock__msg-typing"
            aria-label="正在输入"
          >
            <span class="ik-knock__typing-dot" />
            <span class="ik-knock__typing-dot" />
            <span class="ik-knock__typing-dot" />
          </span>
          <template v-else-if="typeof entry.rendered === 'string' && hasBubbleLinks(entry.rendered)">
            <template v-for="(seg, si) in parseBubbleSegments(entry.rendered)" :key="si">
              <span v-if="seg.type === 'text'">{{ seg.content }}</span>
              <a
                v-else
                :href="seg.href"
                class="ik-knock__msg-link"
                @click="emit('bubble-link', seg.href, $event)"
              >{{ seg.text }}</a>
            </template>
          </template>
          <template v-else>{{ entry.rendered }}</template>
          <span v-if="entry.msg.editedAt && !entry.msg.deletedAt" class="ik-knock__msg-edited">(已编辑)</span>
        </div>
        <!-- 通知 quote 卡：点击跳到关联委托（postModal） -->
        <button
          v-if="entry.quote"
          type="button"
          class="ik-knock__msg-quote"
          @click="emit('quote-click', entry.msg)"
        >
          <DocumentTextIcon
            class="ik-knock__msg-quote-icon"
            aria-hidden="true"
          />
          <span class="ik-knock__msg-quote-text">
            <span class="ik-knock__msg-quote-label">{{ entry.quote.label }}</span>
            <span class="ik-knock__msg-quote-title">
              {{ entry.quote.title }}
            </span>
          </span>
        </button>
        <!-- 3.5 引用帖子：回答里出现过的 /post/xxx，始终展示在气泡下方 -->
        <AiCitationList
          v-if="entry.aiRich && entry.citations.length > 0"
          :citations="entry.citations"
          @open-post="emit('open-post', $event)"
        />
        <!-- 1.4 气泡外左下角：时间 + 复制 + 重新生成，仅 AI 消息展示；始终放在消息块最下方 -->
        <div v-if="entry.aiRich" class="ik-knock__msg-meta" :class="{ 'is-mine': entry.isMine }">
          <span v-if="entry.msg.createdAt" class="ik-knock__msg-meta-time" :title="fullTime || undefined">
            {{ formatTime(entry.msg.createdAt) }}
          </span>
          <button
            v-if="entry.copyable && !entry.aiStreaming"
            type="button"
            class="ik-knock__msg-meta-action"
            :class="{ 'is-done': copiedId === entry.msg.documentId }"
            :aria-label="copiedId === entry.msg.documentId ? '已复制' : '复制消息'"
            :title="copiedId === entry.msg.documentId ? '已复制' : '复制'"
            @click.stop="emit('copy', entry.msg)"
          >
            <CheckIcon
              v-if="copiedId === entry.msg.documentId"
              class="ik-knock__msg-meta-icon"
              aria-hidden="true"
            />
            <ClipboardDocumentIcon
              v-else
              class="ik-knock__msg-meta-icon"
              aria-hidden="true"
            />
          </button>
          <button
            v-if="showRegenerate"
            type="button"
            class="ik-knock__msg-meta-action"
            :disabled="regenerating"
            aria-label="重新生成"
            title="重新生成"
            @click.stop="emit('regenerate', entry.msg)"
          >
            <ArrowPathIcon
              class="ik-knock__msg-meta-icon"
              :class="{ 'is-spinning': regenerating }"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped>
/* 附图：气泡上方的缩略图网格，点开看大图 */
.ik-knock__msg-images {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 330px;
}

/* 自己发的：与右侧气泡对齐 */
.ik-knock__msg.is-mine .ik-knock__msg-images {
  align-self: flex-end;
  justify-content: flex-end;
}

.ik-knock__msg-image {
  display: block;
  flex: 0 0 auto;
  width: 104px;
  height: 104px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  cursor: zoom-in;
}

.ik-knock__msg-image-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* system 分界提示（如「对话已重置」3.3.4） */
.ik-knock__sys-divider {
  display: flex;
  justify-content: center;
  margin: 10px 0;
}

.ik-knock__sys-divider span {
  font-size: 12px;
  color: #888;
  background: rgba(255, 255, 255, 0.05);
  padding: 3px 12px;
  border-radius: 10px;
}

/* 时间分隔行（QQ 风格）：居中、灰色、小字 */
.ik-knock__time-divider {
  align-self: center;
  margin: 6px 0 2px;
  padding: 2px 10px;
  color: rgba(255, 255, 255, 0.32);
  font-size: 12px;
  letter-spacing: 0.5px;
  user-select: none;
}

.ik-knock__msg {
  display: flex;
  /* 参考 chat-generator：头像与气泡间留出昵尖角的位置 */
  gap: 10px;
  align-items: flex-start;
}

.ik-knock__msg-avatar {
  flex-shrink: 0;
  /* 与侧栏会话列表头像保持同尺寸 */
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  color: #4a4a4a;
  overflow: hidden;
}

/* 消息头像可点击跳转个人主页 */
.ik-knock__msg-avatar.is-clickable {
  cursor: pointer;
  transition: opacity 140ms ease;
}

.ik-knock__msg-avatar.is-clickable:hover {
  opacity: 0.8;
}

.ik-knock__msg-avatar.is-clickable:focus-visible {
  outline: 2px solid #fbfe00;
  outline-offset: 2px;
}

.ik-knock__msg-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
}

.ik-knock__msg-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* 限制气泡最大宽度，长消息换行不顶满整列 */
  max-width: min(560px, 92%);
}

/* AI 富文本气泡：代码块 / 表格需要更宽的展示区（:has 不支持时回退 560px） */
.ik-knock__msg-body:has(.ik-ai-md) {
  max-width: min(680px, 94%);
}

.ik-knock__msg-bubble {
  position: relative;
  /* w-fit 自适应内容宽度 */
  align-self: flex-start;
  max-width: 100%;
  /* 参考 chat-generator： 0.3125em 0.75em、圆角 0.9375em，与气泡字号成比 */
  padding: 6px 14px;
  background: #ffffff;
  border-radius: 16px;
  color: #4d4d4d;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  word-break: break-word;
  white-space: pre-wrap;
}

/*
 * 左上昵尖角（bubble nipple）：复用 zenless-tools/chat-generator 原版 webp
 * 参考 ChatGeneratorItemArrow.tsx：top: 0.125em; left: -0.4375em; width/height: 0.75em
 */
.ik-knock__msg-bubble::before {
  content: "";
  position: absolute;
  top: 0.125em;
  left: -0.4375em;
  width: 0.75em;
  height: 0.75em;
  background-image: url("/images/chat_message_arrow_left.webp");
  background-size: contain;
  background-repeat: no-repeat;
  pointer-events: none;
}

/*
 * 白底气泡里的 @mention 芯片需要单独配色：
 * 默认 MentionChip 是「黄绿字 + 透明底」，在白底上几乎不可读。
 * 这里用 :deep 穿透 scoped 边界，把它改成「黄底黑字小标签」——
 * 与 KnockKnock 选中态的 #fbfe00 主色一致，整体语言统一。
 */
.ik-knock__msg-bubble :deep(.ik-mention) {
  background-color: #fbfe00;
  color: #000;
  padding: 0 6px;
  border-radius: 4px;
  font-weight: 700;
}

.ik-knock__msg-bubble :deep(.ik-mention:hover),
.ik-knock__msg-bubble :deep(.ik-mention:focus-visible) {
  background-color: #e8eb00;
  color: #000;
}

/* ── 消息入场动画（仅增量到达的新消息，仅动画 transform + opacity 不触发重排） ──
 * 仿 Inspira「Animated List」的弹簧入场：从气泡一侧的底角缩放弹出，略微回弹。 */
@keyframes ik-msg-enter {
  0% {
    opacity: 0;
    transform: translateY(12px) scale(0.85);
  }
  60% {
    opacity: 1;
    transform: translateY(-2px) scale(1.02);
  }
  100% {
    transform: none;
  }
}

@keyframes ik-divider-enter {
  from {
    opacity: 0;
  }
}

.ik-knock__msg.is-new {
  transform-origin: bottom left;
  animation: ik-msg-enter 420ms cubic-bezier(0.2, 0.9, 0.3, 1) both;
}

.ik-knock__msg.is-new.is-mine {
  transform-origin: bottom right;
}

.ik-knock__time-divider.is-new {
  animation: ik-divider-enter 300ms ease-out both;
}

/* 流式占位气泡内的加载点（首个 delta 未到达前） */
.ik-knock__msg-typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 1.2em;
  vertical-align: middle;
}

.ik-knock__typing-dot {
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  /* 气泡内的加载点需要用深色（白色点在白底气泡上不可见） */
  background: rgba(0, 0, 0, 0.35);
  animation: ik-typing-bounce 1.2s ease-in-out infinite;
}

.ik-knock__typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.ik-knock__typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes ik-typing-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.45; }
  30% { transform: translateY(-4px); opacity: 1; }
}

/* 引用委托卡片：与正常 DM 区分，hint 标签 + 标题 + 文档图标 */
.ik-knock__msg-quote {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  margin: 0;
  padding: 7px 15px;
  border: 0;
  border-radius: 999px;
  background: #000;
  color: #c8c8c8;
  cursor: pointer;
  text-align: left;
  transition: background-color 140ms ease, box-shadow 140ms ease;
}

.ik-knock__msg-quote:hover {
  /* 与侧栏会话项 hover 一致：浅白底 + 黑/白双层 inset ring */
  background-color: rgba(255, 255, 255, 0.04);
  box-shadow:
    inset 0 0 0 1px #000,
    inset 0 0 0 5px rgba(255, 255, 255, 0.35);
}

.ik-knock__msg-quote-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: rgba(251, 254, 0, 0.85);
}

.ik-knock__msg-quote-text {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.ik-knock__msg-quote-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 1px;
}

.ik-knock__msg-quote-title {
  font-size: 13px;
  color: #fff;
  font-weight: 600;
  /* 单行 ellipsis，避免长标题撑爆气泡 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* ── AI 回复内 markdown 链接样式（白底气泡上需要深色链接） ── */
.ik-knock__msg-link {
  color: #2c58e2;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  transition: color 120ms ease;
}

.ik-knock__msg-link:hover {
  color: #1a3fad;
}

/* 自己发的消息：头像 + 气泡整体右对齐；nipple 改右上 */
.ik-knock__msg.is-mine {
  flex-direction: row-reverse;
}

.ik-knock__msg.is-mine .ik-knock__msg-body {
  align-items: flex-end;
}

.ik-knock__msg.is-mine .ik-knock__msg-bubble {
  align-self: flex-end;
  /* 参考 zenless-tools chat-generator：Right 侧 accent-dark (#2c58e2) + 白字
     是 ZZZ 游戏内蓝色对话框的视觉语言；之前的黄底是项目自创版本，统一回原版 */
  background: #2c58e2;
  color: #fff;
}

/* 右侧 nipple：使用专门的右箭头 webp（zenless-tools chat_message_arrow_right.webp），
   注意右侧偏移 -0.34375em 与左侧 -0.4375em 不同——原始资源箭头形状不对称 */
.ik-knock__msg.is-mine .ik-knock__msg-bubble::before {
  left: auto;
  right: -0.34375em;
  background-image: url("/images/chat_message_arrow_right.webp");
  transform: none;
}

/* 撤回的消息：灰色斜体小占位 */
.ik-knock__msg-bubble.is-deleted {
  background: rgba(255, 255, 255, 0.06) !important;
  color: rgba(255, 255, 255, 0.45) !important;
  font-style: italic;
  font-weight: 500;
}

.ik-knock__msg-bubble.is-deleted::before {
  display: none;
}

/* "(已编辑)" 小标 */
.ik-knock__msg-edited {
  margin-left: 6px;
  font-size: 11px;
  font-weight: 600;
}

/* 自己气泡是蓝底白字 → "(已编辑)" 用半透明白保持层级 */
.ik-knock__msg.is-mine .ik-knock__msg-edited {
  color: rgba(255, 255, 255, 0.7);
}

/* 对端气泡是白底，"(已编辑)"用浅灰 */
.ik-knock__msg:not(.is-mine) .ik-knock__msg-edited {
  color: rgba(0, 0, 0, 0.35);
}

/* ── 1.4 气泡外左下角：时间 + 复制 + 重新生成 ── */
/* 气泡下的功能栏：顶栏 tab 条同款的黑底点阵小胶囊，时间 + 圆形图标钮 + 模型标签 */
.ik-knock__msg-meta {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 3px;
  border: 2px solid #2a2a2a;
  border-radius: 999px;
  background: #050505 url("/images/tab-bg-point.webp") repeat;
  color: rgba(255, 255, 255, 0.45);
  font-size: 12px;
  line-height: 1;
  transition: border-color 140ms ease;
}

.ik-knock__msg:hover .ik-knock__msg-meta {
  border-color: #3a3a3a;
}

.ik-knock__msg-meta-time {
  padding: 0 6px 0 8px;
  font-weight: 600;
  user-select: none;
}

.ik-knock__msg-meta-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #1c1c1c;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: color 120ms ease, background-color 120ms ease;
}

.ik-knock__msg-meta-action:hover:not(:disabled) {
  color: #000;
  background: #fbfe00;
}

.ik-knock__msg-meta-action:focus-visible {
  outline: 2px solid #fbfe00;
  outline-offset: 1px;
}

.ik-knock__msg-meta-action.is-done {
  color: #000;
  background: #52d273;
}

.ik-knock__msg-meta-icon {
  width: 14px;
  height: 14px;
}

.ik-knock__msg.is-mine .ik-knock__msg-meta {
  align-self: flex-end;
}

/* 重新生成：请求进行中禁点 + 图标旋转 */
.ik-knock__msg-meta-action:disabled {
  opacity: 0.55;
  cursor: default;
}

.ik-knock__msg-meta-icon.is-spinning {
  animation: ik-spin 0.8s linear infinite;
}

@keyframes ik-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .ik-knock__msg.is-new,
  .ik-knock__time-divider.is-new {
    animation: none;
  }
}
</style>
